/**
 * Wallpaper Detail Page Module
 * Full-page wallpaper view with routing support (like Unsplash)
 */

import {
  getWallpaperById,
  trackDownload,
  toggleLike,
  isLiked,
  rateWallpaper,
  getUserRating,
  getWallpaperStats,
  getRelatedWallpapers
} from './data.js';
import { getModalUrl, getOriginalUrl, getThumbnailUrl, triggerDownload } from './imagekitHelper.js';
import router from './router.js';

function setMeta(name, content, attr = 'name') {
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonical(path) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = `https://anilora.com${path}`;
}

class WallpaperPage {
  constructor() {
    this.container = null;
    this.currentWallpaper = null;
    this.currentRating = 0;
  }

  /**
   * Render the wallpaper detail page
   */
  async render(wallpaperId) {
    console.log('WallpaperPage: render called with id:', wallpaperId);
    const wallpaper = getWallpaperById(wallpaperId);

    if (!wallpaper) {
      console.error('Wallpaper not found:', wallpaperId);
      router.navigate('/', {}, true);
      return;
    }

    console.log('WallpaperPage: rendering wallpaper:', wallpaper.title);

    // Hide gallery and hero IMMEDIATELY to show transition is happening
    const app = document.getElementById('app');
    const hero = document.getElementById('hero');
    if (app) app.style.display = 'none';
    if (hero) hero.style.display = 'none';

    this.currentWallpaper = wallpaper;
    this.currentRating = 0; // Default, will update async

    // Fetch real-time stats from Supabase TRULY non-blocking
    // Don't await - let the page render immediately
    Promise.all([
      getWallpaperStats(wallpaper.id),
      getUserRating(wallpaper.id)
    ]).then(([liveStats, userRating]) => {
      if (liveStats) {
        wallpaper.downloads = liveStats.downloads;
        wallpaper.likes = liveStats.likes;
        wallpaper.rating = liveStats.rating;
        wallpaper.voteCount = liveStats.voteCount;

        // Update UI with fresh stats
        const ratingDisplay = this.container?.querySelector('.rating-display');
        const votesLabel = this.container?.querySelector('.stat-label');
        const likesCount = this.container?.querySelector('.likes-count');
        const downloadsCount = this.container?.querySelector('.downloads-count');

        if (ratingDisplay) ratingDisplay.textContent = wallpaper.rating.toFixed(1);
        if (votesLabel) votesLabel.textContent = `(${wallpaper.voteCount} votes)`;
        if (likesCount) likesCount.textContent = wallpaper.likes;
        if (downloadsCount) downloadsCount.textContent = wallpaper.downloads;
      }

      if (userRating) {
        this.currentRating = userRating;
        // Update star rating
        const stars = this.container?.querySelectorAll('.star');
        stars?.forEach((star, index) => {
          star.classList.toggle('active', index < userRating);
        });
      }
    }).catch(e => {
      console.warn('Could not fetch live stats:', e);
    });

    // Create container if it doesn't exist
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'wallpaper-page';
      this.container.className = 'wallpaper-page';
      // Set up event delegation ONCE on the container — survives innerHTML replacements
      this.setupEventListeners();
    }

    // Build page HTML
    const imageUrl = getModalUrl(wallpaper.category, wallpaper.filename);
    this.updateMetadata(wallpaper, imageUrl);
    const pageHTML = `
      <div class="wallpaper-page-header">
        <button class="back-btn" id="back-btn" aria-label="Go back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </button>
        <h1 class="wallpaper-page-title">${wallpaper.title}</h1>
      </div>

      <!-- Top Banner Ad -->
      <div class="wp-ad-banner" id="wp-ad-top"></div>

      <div class="wp-content-ad-wrapper">
      <div class="wp-main-content">
      <div class="wallpaper-page-content">
        <!-- Left: Image Preview -->
        <div class="wallpaper-image-section">
          <img src="${imageUrl}" alt="${wallpaper.title}" class="wallpaper-image">
          <div class="wallpaper-colors">
            <span class="color-label">Colors:</span>
            <div class="color-palette">
              ${wallpaper.colors.map(color => `<span class="color-swatch" style="background-color: ${color}"></span>`).join('')}
            </div>
          </div>
        </div>

        <!-- Right: Details & Actions -->
        <div class="wallpaper-details-section">
          <!-- Stats Bar -->
          <div class="wallpaper-stats">
            <span class="stat">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
              <span class="rating-display">${wallpaper.rating.toFixed(1)}</span>
              <span class="stat-label">(${wallpaper.voteCount} votes)</span>
            </span>
            <span class="stat">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
              <span class="likes-count">${wallpaper.likes}</span>
            </span>
            <span class="stat">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z"/>
              </svg>
              <span class="downloads-count">${wallpaper.downloads}</span>
            </span>
          </div>

          <!-- Rating Widget -->
          <div class="wallpaper-rating">
            <span class="rating-label">Your Rating:</span>
            <div class="star-rating" id="star-rating">
              ${[1, 2, 3, 4, 5].map(i => `
                <button class="star ${i <= this.currentRating ? 'active' : ''}" data-rating="${i}" aria-label="Rate ${i} star${i > 1 ? 's' : ''}">★</button>
              `).join('')}
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="wallpaper-actions">
            <button id="like-btn" class="action-btn like-btn" aria-label="Like this wallpaper">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
              <span>Like</span>
            </button>
            <button id="share-btn" class="action-btn share-btn" aria-label="Share this wallpaper">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
              <span>Share</span>
            </button>
          </div>

          <!-- Download Section -->
          <div class="wallpaper-download">
            <h3 class="download-title">Download</h3>
            <div class="variant-options">
              ${this.renderVariantOptions(wallpaper)}
            </div>
          </div>

          <!-- Info Section -->
          <div class="wallpaper-info">
            <div class="info-row">
              <span class="info-label">Category:</span>
              <span class="info-value">${wallpaper.category}</span>
            </div>
            ${wallpaper.subcategory ? `
              <div class="info-row">
                <span class="info-label">Series:</span>
                <span class="info-value">${wallpaper.subcategory.replace(/-/g, ' ')}</span>
              </div>
            ` : ''}
            <div class="info-row">
              <span class="info-label">Resolution:</span>
              <span class="info-value">${wallpaper.resolution}</span>
            </div>
            <div class="info-row">
              <span class="info-label">File Size:</span>
              <span class="info-value">${wallpaper.fileSize}</span>
            </div>
          </div>

          <!-- Tags Section -->
          <div class="wallpaper-tags">
            <h3 class="tags-title">Tags</h3>
            <div class="tag-list">
              ${wallpaper.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
            </div>
          </div>
        </div>
      </div>

      <!-- Related Wallpapers -->
      <div class="wallpaper-related">
        <h3 class="related-title">More Like This</h3>
        <div id="related-container"></div>
      </div>

      <!-- Native Banner Ad -->
      <div class="native-ad-container">
        <div id="container-41575603342be2dc55c735c9c3eed2d2"></div>
      </div>
      </div><!-- .wp-main-content -->

      <!-- Right Sidebar Ad -->
      <aside class="ad-sidebar wp-ad-sidebar">
        <div class="ad-sidebar__sticky" id="wp-ad-right"></div>
      </aside>
      </div><!-- .wp-content-ad-wrapper -->
    `;

    this.container.innerHTML = pageHTML;

    if (!document.body.contains(this.container)) {
      const footer = document.querySelector('.footer');
      if (footer) {
        document.body.insertBefore(this.container, footer);
      } else {
        document.body.appendChild(this.container);
      }
    }

    // Force visibility and bring to front
    this.container.style.display = 'block';
    this.container.style.position = 'relative';
    this.container.style.zIndex = '1';

    // Scroll to top BEFORE loading content to prevent any race conditions
    window.scrollTo({ top: 0, behavior: 'instant' });

    // Inject ad scripts (innerHTML doesn't execute <script> tags)
    this.injectAds();

    // Load related wallpapers (event delegation handles their clicks automatically)
    this.loadRelatedWallpapers();

    // Update like button state (async)
    this.updateLikeStatus(wallpaper.id);
  }

  updateMetadata(wallpaper, imageUrl) {
    const path = `/wallpaper/${wallpaper.id}`;
    const category = wallpaper.category.toLowerCase();
    const tags = wallpaper.tags.slice(0, 4).join(', ');
    const description = `Download ${wallpaper.title}, a free ${category} wallpaper for desktop and mobile. ${wallpaper.resolution} resolution, no watermark. Tags: ${tags}.`;

    document.title = `${wallpaper.title} Wallpaper - Free Download | Anilora`;
    setMeta('description', description);
    setMeta('robots', 'index, follow, max-image-preview:large');
    setMeta('og:type', 'article', 'property');
    setMeta('og:title', `${wallpaper.title} Wallpaper - Free Download`, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', `https://anilora.com${path}`, 'property');
    setMeta('og:image', imageUrl, 'property');
    setMeta('twitter:title', `${wallpaper.title} Wallpaper - Free Download`);
    setMeta('twitter:description', description);
    setMeta('twitter:image', imageUrl);
    setCanonical(path);
  }

  async updateLikeStatus(id) {
    try {
      const liked = await isLiked(id);
      const likeBtn = this.container.querySelector('#like-btn');
      if (likeBtn) {
        likeBtn.classList.toggle('liked', liked);
        likeBtn.querySelector('span').textContent = liked ? 'Liked' : 'Like';
      }
    } catch (e) {
      console.warn('Error checking like status:', e);
    }
  }

  renderVariantOptions(wallpaper) {
    const variants = [
      {
        name: 'Original',
        resolution: wallpaper.variants?.original?.resolution || wallpaper.resolution,
        size: wallpaper.variants?.original?.size || wallpaper.fileSize,
        url: wallpaper.variants?.original?.url || wallpaper.filename
      }
    ];

    if (wallpaper.variants?.['1080p']) {
      variants.push({
        name: '1080p',
        resolution: wallpaper.variants['1080p'].resolution,
        size: wallpaper.variants['1080p'].size,
        url: wallpaper.variants['1080p'].url
      });
    }

    return variants.map(variant => `
      <button class="variant-btn" data-variant="${variant.name.toLowerCase()}" data-url="${variant.url}">
        <div class="variant-info">
          <span class="variant-name">${variant.name}</span>
          <span class="variant-resolution">${variant.resolution}</span>
        </div>
        <div class="variant-size">${variant.size}</div>
        <svg class="variant-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
      </button>
    `).join('');
  }

  setupEventListeners() {
    // Use EVENT DELEGATION on the container — a single listener that survives
    // innerHTML replacements and is immune to DOM mutations from downloads.
    this.container.addEventListener('click', (e) => {
      console.log('[WP] click event on:', e.target.tagName, e.target.className);

      // Back button
      if (e.target.closest('#back-btn')) {
        router.back();
        return;
      }

      // Share button
      if (e.target.closest('#share-btn')) {
        this.handleShare();
        return;
      }

      // Like button
      if (e.target.closest('#like-btn')) {
        console.log('[WP] like-btn matched');
        this.handleLike();
        return;
      }

      // Star rating
      const star = e.target.closest('.star');
      if (star) {
        const rating = parseInt(star.dataset.rating);
        console.log('[WP] star matched, rating:', rating);
        this.handleRating(rating);
        return;
      }

      // Download buttons
      const variantBtn = e.target.closest('.variant-btn');
      if (variantBtn) {
        const url = variantBtn.dataset.url;
        console.log('[WP] variant-btn matched, url:', url);
        this.handleDownload(url);
        return;
      }

      // Related wallpaper cards
      const relatedCard = e.target.closest('.related-card');
      if (relatedCard) {
        const id = relatedCard.dataset.id;
        router.navigate(`/wallpaper/${id}`);
        return;
      }
    });
  }

  handleShare() {
    const url = window.location.href;

    // Try native share API first
    if (navigator.share) {
      navigator.share({
        title: this.currentWallpaper.title,
        text: `Check out this wallpaper: ${this.currentWallpaper.title}`,
        url: url
      }).catch(() => {
        // Fallback to clipboard
        this.copyToClipboard(url);
      });
    } else {
      // Fallback to clipboard
      this.copyToClipboard(url);
    }
  }

  copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      // Show success message
      const shareBtn = this.container.querySelector('#share-btn span');
      const originalText = shareBtn.textContent;
      shareBtn.textContent = 'Link Copied!';
      setTimeout(() => {
        shareBtn.textContent = originalText;
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  }

  async handleLike() {
    console.log('[WP] handleLike called, wallpaper id:', this.currentWallpaper.id);
    try {
      const likeBtn = this.container.querySelector('#like-btn');
      const likeBtnText = likeBtn.querySelector('span');
      const likesCount = this.container.querySelector('.likes-count');

      const liked = await toggleLike(this.currentWallpaper.id);
      console.log('[WP] toggleLike returned:', liked);

      likeBtn.classList.toggle('liked', liked);
      likeBtnText.textContent = liked ? 'Liked' : 'Like';

      // Update count
      const wallpaper = getWallpaperById(this.currentWallpaper.id);
      console.log('[WP] wallpaper.likes after toggle:', wallpaper.likes);
      likesCount.textContent = wallpaper.likes;
    } catch (error) {
      console.error('[WP] Failed to toggle like:', error);
      alert('Unable to like wallpaper. Please try again.');
    }
  }

  async handleRating(rating) {
    console.log('[WP] handleRating called with:', rating, 'wallpaper id:', this.currentWallpaper.id);
    try {
      this.currentRating = rating;
      const result = await rateWallpaper(this.currentWallpaper.id, rating);
      console.log('[WP] rateWallpaper returned:', result);

      // Update UI
      const stars = this.container.querySelectorAll('.star');
      stars.forEach((star, index) => {
        star.classList.toggle('active', index < rating);
      });

      // Update rating display
      const wallpaper = getWallpaperById(this.currentWallpaper.id);
      console.log('[WP] wallpaper rating after update:', wallpaper.rating, 'votes:', wallpaper.voteCount);
      const ratingDisplay = this.container.querySelector('.rating-display');
      const voteCount = this.container.querySelector('.stat-label');
      ratingDisplay.textContent = wallpaper.rating.toFixed(1);
      voteCount.textContent = `(${wallpaper.voteCount} votes)`;
    } catch (error) {
      console.error('[WP] Failed to rate wallpaper:', error);
      alert('Unable to rate wallpaper. Please try again.');
    }
  }

  handleDownload(url) {
    // Fire and forget tracking so UI doesn't hang on DB lag
    trackDownload(this.currentWallpaper.id).then(newCount => {
      const downloadsCount = this.container.querySelector('.downloads-count');
      if (downloadsCount) downloadsCount.textContent = newCount;
    }).catch(e => console.warn('Tracking failed:', e));

    const downloadsCount = this.container.querySelector('.downloads-count');
    if (downloadsCount) downloadsCount.textContent = this.currentWallpaper.downloads;

    const cdnUrl = getOriginalUrl(this.currentWallpaper.category, url);
    const displayFilename = url.split('/').pop();
    triggerDownload(cdnUrl, displayFilename);
  }

  loadRelatedWallpapers() {
    // Fetch a larger pool to ensure we have enough for both categories
    const related = getRelatedWallpapers(this.currentWallpaper.id, 20);
    const container = this.container.querySelector('#related-container');

    if (related.length === 0) {
      container.innerHTML = '<p style="color: var(--color-text-secondary);">No related wallpapers found</p>';
      return;
    }

    // Split into landscape and portrait
    const landscapes = [];
    const portraits = [];

    related.forEach(w => {
      let isPortrait = false;
      if (w.resolution) {
        const [width, height] = w.resolution.split('x').map(Number);
        if (!isNaN(width) && !isNaN(height) && height > width) {
          isPortrait = true;
        }
      }
      if (isPortrait) {
        portraits.push(w);
      } else {
        landscapes.push(w);
      }
    });

    // Generate HTML
    let html = '';

    // 1. Landscape Grid (Row 1) - Limit 3
    if (landscapes.length > 0) {
      html += '<div class="related-grid-landscape">';
      html += landscapes.slice(0, 3).map(w => this.createCardHtml(w, 'landscape')).join('');
      html += '</div>';
    }

    // 2. Portrait Grid (Row 2) - Limit 4
    if (portraits.length > 0) {
      html += '<div class="related-grid-portrait">';
      html += portraits.slice(0, 4).map(w => this.createCardHtml(w, 'portrait')).join('');
      html += '</div>';
    }

    container.innerHTML = html;
    // Click listeners are handled by event delegation on this.container
  }

  createCardHtml(wallpaper, type) {
    // Use optimized thumbnail with selective quality (Vertical: 50%, Horizontal: 70%)
    const imageUrl = getThumbnailUrl(wallpaper.category, wallpaper.filename, type === 'portrait');
    return `
      <div class="related-card ${type}" data-id="${wallpaper.id}">
        <div class="card-image-container">
          <img 
            src="${imageUrl}" 
            alt="${wallpaper.title}" 
            loading="lazy"
            onload="this.parentElement.classList.add('loaded')"
          >
          <div class="card-loader"></div>
        </div>
        <div class="related-overlay">
          <h4>${wallpaper.title}</h4>
        </div>
      </div>
    `;
  }

  injectAds() {
    // Temporarily hide gallery's native ad container so the fresh script
    // finds only the wallpaper page container (duplicate IDs cause conflicts)
    const galleryNative = document.getElementById('container-41575603342be2dc55c735c9c3eed2d2');
    if (galleryNative && !this.container.contains(galleryNative)) {
      galleryNative.id = 'container-native-gallery-hidden';
    }

    // Inject banner ads sequentially (share global atOptions variable)
    this.injectAdScript(
      '#wp-ad-top',
      '3afd83ee068db8bc91cb82274d8cc1eb',
      728, 90,
      () => this.injectAdScript('#wp-ad-right', '8f33f507458345b1f3f5c8885bc8ca62', 160, 600,
        () => this.injectNativeBanner()
      )
    );
  }

  injectNativeBanner() {
    const slot = this.container.querySelector('#container-41575603342be2dc55c735c9c3eed2d2');
    if (!slot || slot.hasChildNodes()) return;
    const s = document.createElement('script');
    s.src = 'https://pl28760466.effectivegatecpm.com/41575603342be2dc55c735c9c3eed2d2/invoke.js';
    s.async = true;
    s.setAttribute('data-cfasync', 'false');
    document.head.appendChild(s);
  }

  injectAdScript(selector, key, width, height, onDone) {
    const slot = this.container.querySelector(selector);
    if (!slot || slot.hasChildNodes()) {
      onDone?.();
      return;
    }
    const s1 = document.createElement('script');
    s1.textContent = `atOptions = { 'key': '${key}', 'format': 'iframe', 'height': ${height}, 'width': ${width}, 'params': {} };`;
    const s2 = document.createElement('script');
    s2.src = `https://www.highperformanceformat.com/${key}/invoke.js`;
    s2.onload = () => onDone?.();
    s2.onerror = () => onDone?.();
    slot.appendChild(s1);
    slot.appendChild(s2);
  }

  hide() {
    if (this.container) {
      this.container.style.display = 'none';
    }

    // Restore gallery's native ad container ID
    const hiddenNative = document.getElementById('container-native-gallery-hidden');
    if (hiddenNative) hiddenNative.id = 'container-41575603342be2dc55c735c9c3eed2d2';

    const app = document.getElementById('app');
    const hero = document.getElementById('hero');
    if (app) app.style.display = 'block';
    if (hero) hero.style.display = 'block';
  }
}

export default new WallpaperPage();
